import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { AxiosInstance } from '../../../../utilities/requestClient';
import { getReportsListURL } from '../../../../routes/endpoints/general.routes';
import {
  RSOverviewPageModel,
  RSFundPageModel,
} from '../../../api.types';
import {
  getLasdabCplRiskReportURL,
  getLiquidationRiskOverviewURL,
  getLiquidityModelBreakdownURL,
  getLiquidityModelEvolutionURL,
  getLiquidityOverviewMonitorURL,
  getLiquidityRiskEvolutionURL,
  getMarketLiquidityBidAskURL,
  getMoabReportURL,
  getRstVariableReportURL,
  getSubscriptionsRedemptionsURL,
  getVariableMarketVolumeLiquidationCostsReportURL,
} from '../liquidity.routes';
import type { IMaxmimumOneDayServerData } from '../../../../components/pages/liquidity/liquidationRiskOverview/marketBasedAndMaximum/maximum.data';
import type { EvolutionServerData } from '../../../../components/pages/liquidity/riskEvolution/charts/portfolioLiquidation/portfolioLiquidation.data';
import {
  parseRstVariableResponse,
  type RstVariablePageModel,
} from '../../../../components/pages/liquidity/rstVariable/rstVariable.types';
import { SubscriptionsRedemptionsPageModel } from '../../../../components/pages/ucits/subscriptionsRedemptions/subscriptionsRedemptions.types';
import { LasdabCplRiskPageModel } from '../../../../components/pages/liquidity/lasdab/lasdab.types';
import { MoabReport } from '../../../../components/pages/liquidity/moab/moab.types';
import type { VariableMarketVolumeLiquidationCostsPageModel } from '../../../../components/pages/liquidity/variableMarketVolumeLiquidationCosts/variableMarketVolumeLiquidationCosts.types';

export interface UseLiquidityReportsListQueryParams {
  fundTypes?: string;
  raptorPlatformId?: string;
}

export const useLiquidityReportsListQuery = (
  params: UseLiquidityReportsListQueryParams,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<any, Error>({
    queryKey: [
      'liquidity-reports-list',
      params.fundTypes,
      params.raptorPlatformId,
    ],
    queryFn: async () => {
      const url = `/${getReportsListURL(
        params.fundTypes,
        'daily_liquidity_risk_report',
        params.raptorPlatformId,
      )}`;
      const { data } = await AxiosInstance.get<any>(url);
      return data;
    },
    enabled: !!params.fundTypes && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export interface UseLiquidityOverviewMonitorQueryParams {
  positionDate?: string;
  fundTypes?: string;
  raptorPlatformId?: string;
}

export const useLiquidityOverviewMonitorQuery = (
  params: UseLiquidityOverviewMonitorQueryParams,
  options?: Omit<
    UseQueryOptions<RSOverviewPageModel<any[]>, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<RSOverviewPageModel<any[]>, Error>({
    queryKey: [
      'liquidity-overview-monitor',
      params.positionDate,
      params.fundTypes,
      params.raptorPlatformId,
    ],
    queryFn: async () => {
      const url = `/${getLiquidityOverviewMonitorURL(
        params.positionDate,
        params.fundTypes,
        params.raptorPlatformId,
      )}`;
      const { data } = await AxiosInstance.get<RSOverviewPageModel<any[]>>(url);
      return data;
    },
    enabled:
      !!params.fundTypes && !!params.positionDate && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export type UseStoredAggregatedReportPageModelOptions<
  TPage extends RSFundPageModel,
> = Omit<
  UseQueryOptions<TPage | null, Error>,
  'queryKey' | 'queryFn' | 'enabled' | 'staleTime'
> & {
  queryKey: readonly unknown[];
  enabled?: boolean;
  staleTime?: number;
};

/**
 * GET a stored-aggregated report URL and parse as `RSFundPageModel`.
 * Prefer named URL helpers in `liquidity.routes.ts` (e.g. `getSubscriptionsRedemptionsURL`) when wiring hooks.
 */
export function useStoredAggregatedReportPageModel<
  TPage extends RSFundPageModel = RSFundPageModel,
>(url: string, options: UseStoredAggregatedReportPageModelOptions<TPage>) {
  const { queryKey, enabled, staleTime, ...rest } = options;

  return useQuery<TPage | null, Error>({
    ...rest,
    queryKey,
    queryFn: async () => {
      const { data } = await AxiosInstance.get<TPage>(url);
      return data;
    },
    enabled: enabled ?? true,
    staleTime: staleTime ?? 5 * 60 * 1000,
  });
}

export function useSubscriptionsRedemptionsPageModel(
  fundId: string,
  positionDate?: string,
) {
  const url = getSubscriptionsRedemptionsURL(fundId, positionDate);
  return useStoredAggregatedReportPageModel<SubscriptionsRedemptionsPageModel>(
    url,
    {
      queryKey: ['subscriptions-redemptions', fundId, positionDate],
      enabled: !!fundId,
    },
  );
}

export function useLasdabPageModel(fundId: string, positionDate?: string) {
  const url = getLasdabCplRiskReportURL(fundId, positionDate);
  return useStoredAggregatedReportPageModel<LasdabCplRiskPageModel>(url, {
    queryKey: ['lasdab-data', fundId, positionDate],
    enabled: !!fundId,
  });
}

export function useMoabPageModel(fundId: string, positionDate?: string) {
  const url = getMoabReportURL(fundId, positionDate);
  return useStoredAggregatedReportPageModel<RSFundPageModel<MoabReport>>(url, {
    queryKey: ['moab-data', fundId, positionDate],
    enabled: !!fundId,
  });
}

export function useVariableMarketVolumeLiquidationCostsPageModel(
  fundId: string,
  positionDate?: string,
) {
  const url = getVariableMarketVolumeLiquidationCostsReportURL(
    fundId,
    positionDate,
  );
  return useStoredAggregatedReportPageModel<VariableMarketVolumeLiquidationCostsPageModel>(
    url,
    {
      queryKey: [
        'variable-market-volume-liquidation-costs',
        fundId,
        positionDate,
      ],
      enabled: !!fundId,
    },
  );
}

export type LiquidationRiskOverviewPageModel =
  RSFundPageModel<IMaxmimumOneDayServerData>;

export function useLiquidationRiskOverviewPageModel(
  fundId: string,
  positionDate?: string,
) {
  const url = getLiquidationRiskOverviewURL(fundId, positionDate);

  return useQuery({
    queryKey: ['liquidation-risk-overview', fundId, positionDate] as const,
    queryFn: async () => {
      const { data } =
        await AxiosInstance.get<LiquidationRiskOverviewPageModel>(url);
      return data;
    },
    enabled: !!fundId,
    staleTime: 1000 * 60 * 5,
  });
}

export type LiquidityModelBreakdownPayload = unknown;
export type LiquidityModelBreakdownPageModel =
  RSFundPageModel<LiquidityModelBreakdownPayload>;

export function useLiquidityModelBreakdownPageModel(input: {
  fundId: string;
  positionDate?: string;
  participationRate?: string;
}) {
  const { fundId, positionDate, participationRate } = input;

  return useQuery({
    queryKey: [
      'liquidity-model-breakdown',
      fundId,
      positionDate,
      participationRate ?? null,
    ] as const,
    queryFn: async () => {
      const url = getLiquidityModelBreakdownURL({
        fundId,
        positionDate: positionDate!,
        participationRate,
      });
      const { data } =
        await AxiosInstance.get<LiquidityModelBreakdownPageModel>(url);
      return data;
    },
    enabled: !!fundId && !!positionDate,
    staleTime: 1000 * 60 * 5,
  });
}

export type LiquidityModelEvolutionCell = {
  used_volume?: number;
  used_days?: number;
};

export type LiquidityModelEvolutionRow = {
  position_name: string;
  [date: string]: unknown;
};

export type LiquidityModelEvolutionPayload = {
  dates: string[];
  liquidity_table: LiquidityModelEvolutionRow[];
};

export type LiquidityModelEvolutionPageModel =
  RSFundPageModel<LiquidityModelEvolutionPayload>;

export function useLiquidityModelEvolutionPageModel(input: {
  fundId: string;
  startDate: string | null;
  endDate: string | null;
  participationRate?: string;
}) {
  const { fundId, startDate, endDate, participationRate } = input;

  return useQuery({
    queryKey: [
      'liquidity-model-evolution',
      fundId,
      startDate,
      endDate,
      participationRate ?? null,
    ] as const,
    queryFn: async () => {
      const url = getLiquidityModelEvolutionURL({
        fundId,
        startDate: startDate!,
        endDate: endDate!,
        participationRate,
      });
      const { data } =
        await AxiosInstance.get<LiquidityModelEvolutionPageModel>(url);
      return data;
    },
    enabled: !!fundId && !!startDate && !!endDate,
    staleTime: 1000 * 60 * 5,
  });
}

export function useMarketLiquidityRiskPageModel(
  fundId: string,
  positionDate?: string | null,
) {
  const url = getMarketLiquidityBidAskURL(fundId, positionDate);

  return useQuery({
    queryKey: ['market-liquidity-risk', fundId, positionDate ?? null] as const,
    queryFn: async () => {
      const { data } = await AxiosInstance.get<RSFundPageModel>(url);
      return data;
    },
    enabled: !!fundId,
    staleTime: 1000 * 60 * 5,
  });
}

export type LiquidityRiskEvolutionPageModel =
  RSFundPageModel<EvolutionServerData>;

export function useLiquidityRiskEvolutionPageModel(
  fundId: string,
  date1: string | null | undefined,
  date2: string | null | undefined,
) {
  return useQuery({
    queryKey: ['liquidity-risk-evolution', fundId, date1, date2] as const,
    queryFn: async () => {
      const url = getLiquidityRiskEvolutionURL(fundId, date1!, date2!);
      const { data } =
        await AxiosInstance.get<LiquidityRiskEvolutionPageModel>(url);
      return data;
    },
    enabled: !!fundId && !!date1 && !!date2,
    staleTime: 1000 * 60 * 5,
  });
}

export function useRstVariablePageModel(fundId: string, positionDate?: string) {
  const url = getRstVariableReportURL(fundId, positionDate);

  return useQuery<RstVariablePageModel | null>({
    queryKey: ['rst-variable-data', fundId, positionDate] as const,
    queryFn: async () => {
      const { data } = await AxiosInstance.get<unknown>(url);
      return parseRstVariableResponse(data);
    },
    enabled: !!fundId,
    staleTime: 1000 * 60 * 5,
  });
}
